import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { toast } from 'react-hot-toast';
import { Plus, Search, RefreshCw, Loader, Cpu, Zap } from 'lucide-react';
import type { Database } from '../../types/supabase';
import { MinerDataManager } from '../../components/admin/MinerDataManager';

type Miner = Database['public']['Tables']['miners']['Row'];

export function MinerManagement() {
  const [miners, setMiners] = useState<Miner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchMiners();
  }, []);

  const fetchMiners = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await supabase
        .from('miners')
        .select('*')
        .order('release_year', { ascending: false });

      if (error) throw error;
      setMiners(data || []);
    } catch (error: any) {
      console.error('Error fetching miners:', error);
      toast.error('Failed to load miners');
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  const filteredMiners = miners.filter(miner =>
    miner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    miner.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    miner.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const minersByAlgorithm = filteredMiners.reduce((acc, miner) => {
    const algorithm = miner.algorithm;
    if (!acc[algorithm]) {
      acc[algorithm] = [];
    }
    acc[algorithm].push(miner);
    return acc;
  }, {} as Record<string, typeof filteredMiners>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Data Management Section */}
      <MinerDataManager />

      {/* Miners List Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search miners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={fetchMiners}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => {/* TODO: Implement add miner */}}
              className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Miner
            </button>
          </div>
        </div>

        {Object.entries(minersByAlgorithm).map(([algorithm, algorithmMiners]) => (
          <div key={algorithm} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{algorithm}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manufacturer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hashrate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Power
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Efficiency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Release Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {algorithmMiners.map((miner) => (
                    <tr key={miner.model} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Cpu className="w-5 h-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{miner.model}</div>
                            <div className="text-sm text-gray-500">{miner.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{miner.manufacturer}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Zap className="w-4 h-4 text-yellow-500 mr-2" />
                          <span className="text-sm text-gray-900">
                            {miner.hashrate} {miner.hashrate_unit}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{miner.power}W</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {(miner.hashrate / miner.power).toFixed(3)} {miner.hashrate_unit}/W
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{miner.release_year}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          miner.status === 'current' ? 'bg-green-100 text-green-800' :
                          miner.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {miner.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}